/**
 * Shapes returned by the site-credentials endpoints
 * (`competition_insights.modules.site_credentials.schemas`). Every credential
 * is server-side: `ingest` is the WordPress connector's, `read` is a website's
 * server reading its own figures back (the Companion app). There is still no
 * browser-kind credential to model.
 *
 * `last_used_at` is not tracked by the API (the self-lookup documents it as
 * always null), so it is not part of this shape either.
 */

import type { Environment } from '@/api/types'

/** Mirrors `SiteCredentialKind`. */
export type SiteCredentialKind = 'ingest' | 'read'

/** Mirrors `SiteCredentialStatus`. */
export type SiteCredentialStatus = 'active' | 'rotating' | 'revoked'

/** Mirrors `SiteCredentialOut`. */
export interface SiteCredential {
  id: string
  website_id: string
  environment: Environment
  /** Absent from an API that predates read credentials: every one of those is `ingest`. */
  kind?: SiteCredentialKind
  label: string
  status: SiteCredentialStatus
  created_at: string
  rotated_at: string | null
  revoked_at: string | null
  overlap_until: string | null
}

/** Mirrors `SiteCredentialListOut`. */
export interface SiteCredentialListOut {
  items: SiteCredential[]
}

/** Mirrors `IssuedSiteCredentialOut` — the one response that carries the token. */
export interface IssuedSiteCredential extends SiteCredential {
  token: string
  token_type: 'Bearer'
}

export const KIND_LABELS: Record<SiteCredentialKind, string> = {
  ingest: 'Server (WordPress connector)',
  read: 'Server, read-only (Companion app)',
}

/** The label a credential's issuer starts from, by kind. The API defaults to the same. */
export const DEFAULT_LABELS: Record<SiteCredentialKind, string> = {
  ingest: 'WordPress connector',
  read: 'Companion app (read)',
}

export function kindOf(credential: SiteCredential): SiteCredentialKind {
  return credential.kind ?? 'ingest'
}

export const STATUS_LABELS: Record<SiteCredentialStatus, string> = {
  active: 'Active',
  rotating: 'Rotating',
  revoked: 'Revoked',
}

export function isRevoked(credential: SiteCredential): boolean {
  return credential.status === 'revoked'
}
